// =============================================================================
// ui-store.ts — UI state management (Zustand)
// Project: Texas Hold'em Social Casino
// =============================================================================
// Manages screen navigation, modals, toasts, and audio settings.
// This is separate from game state to keep concerns clean.

import { create } from 'zustand'
import { Screen } from '../types'

interface UIState {
  // Navigation
  currentScreen: Screen
  previousScreen: Screen | null

  // Modals
  showSettings: boolean
  showBuyChips: boolean
  showHowToPlay: boolean

  // Toast notifications
  toasts: { id: string; message: string; type: 'info' | 'success' | 'error'; duration: number }[]

  // Settings
  soundEnabled: boolean
  musicEnabled: boolean
  animationsEnabled: boolean
  volume: number  // 0-100

  // Responsive
  isMobile: boolean

  // Actions
  navigateTo: (screen: Screen) => void
  goBack: () => void
  toggleSettings: () => void
  toggleBuyChips: () => void
  toggleHowToPlay: () => void
  showToast: (message: string, type?: 'info' | 'success' | 'error', duration?: number) => void
  dismissToast: (id: string) => void
  setSoundEnabled: (enabled: boolean) => void
  setMusicEnabled: (enabled: boolean) => void
  setAnimationsEnabled: (enabled: boolean) => void
  setVolume: (volume: number) => void
  setIsMobile: (isMobile: boolean) => void
  reset: () => void
}

export const useUIStore = create<UIState>((set, get) => ({
  currentScreen: 'loading',
  previousScreen: null,
  showSettings: false,
  showBuyChips: false,
  showHowToPlay: false,
  toasts: [],
  soundEnabled: true,
  musicEnabled: true,
  animationsEnabled: true,
  volume: 80,
  isMobile: window.innerWidth < 768,

  navigateTo: (screen) => {
    const prev = get().currentScreen
    set({ currentScreen: screen, previousScreen: prev })
  },

  goBack: () => {
    const prev = get().previousScreen
    if (prev) set({ currentScreen: prev, previousScreen: null })
  },

  toggleSettings: () => set(s => ({ showSettings: !s.showSettings })),
  toggleBuyChips: () => set(s => ({ showBuyChips: !s.showBuyChips })),
  toggleHowToPlay: () => set(s => ({ showHowToPlay: !s.showHowToPlay })),

  showToast: (message, type = 'info', duration = 3000) => {
    const id = crypto.randomUUID()
    set(s => ({ toasts: [...s.toasts, { id, message, type, duration }] }))
    setTimeout(() => get().dismissToast(id), duration)
  },

  dismissToast: (id) => {
    set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }))
  },

  setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),
  setMusicEnabled: (enabled) => set({ musicEnabled: enabled }),
  setAnimationsEnabled: (enabled) => set({ animationsEnabled: enabled }),
  setVolume: (volume) => set({ volume: Math.max(0, Math.min(100, volume)) }),
  setIsMobile: (isMobile) => set({ isMobile }),

  reset: () => set({
    currentScreen: 'lobby',
    previousScreen: null,
    showSettings: false,
    showBuyChips: false,
    showHowToPlay: false,
    toasts: [],
  }),
}))
